import {isUndefined, noop} from 'lodash';
import {FC, useEffect} from 'react';
import {Subject, timer} from 'rxjs';
import {concatMap, distinctUntilChanged, switchMap} from 'rxjs/operators';

import {useConstant} from '../../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/reduxHelpers';
import {MarkAsReadDelaysEnum} from '../../../../../models/api/preferencesModel';
import {toggleMarkConversationAsSeenAction} from '../../../../../store/data/conversations/seenConversationsActions';
import {getIsImpersonating} from '../../../../../store/data/dataSelectors';
import {getMarkAsReadDelay} from '../../../../../store/data/preferences/preferencesSelectors';
import {CellsContext} from '../../../../../store/panels/cells/context/cellsContext';

/*
 * Props.
 */

interface ConversationTimelineMarkAsReadOnLoadProps {
  cellsContext: CellsContext;
  conversationId: number;
}

/*
 * Component.
 */

// TODO: Remove this once ConversationTimeline can use the hook directly.
export const ConversationTimelineMarkAsReadOnLoad: FC<ConversationTimelineMarkAsReadOnLoadProps> = (
  props,
) => {
  useMarkAsReadOnLoad(props.cellsContext, props.conversationId);

  return null;
};

/*
 * Hook.
 */

interface MarkAsReadOnLoadParams {
  conversationId: number;
  cellsContext: CellsContext;
}

const markAsReadOnLoadDelayTime: {readonly [T in MarkAsReadDelaysEnum]?: number} = {
  [MarkAsReadDelaysEnum.ON_MESSAGE_OPEN]: 0,
  [MarkAsReadDelaysEnum.ONE_SECOND]: 1000,
  [MarkAsReadDelaysEnum.TWO_SECONDS]: 2000,
  [MarkAsReadDelaysEnum.FIVE_SECONDS]: 5000,
};

// TODO: De-dupe with MobileViewerTimelineMarkAsReadContainer.
export function useMarkAsReadOnLoad(cellsContext: CellsContext, conversationId: number): void {
  const dispatch = useAppDispatch();
  const isImpersonating = useAppSelector(getIsImpersonating);
  const markAsReadDelay = useAppSelector(getMarkAsReadDelay);

  // Create a stream of mark as read requests.
  const params$ = useConstant(() => new Subject<MarkAsReadOnLoadParams>());

  // Process the stream.
  useEffect(() => {
    if (isImpersonating) {
      return noop;
    }

    // Find out if and how long after loading a conversation we should mark as read.
    const delayTime = markAsReadOnLoadDelayTime[markAsReadDelay];

    if (isUndefined(delayTime)) {
      return noop;
    }

    const subscription = params$
      .pipe(
        // Only mark as read when the conversation and/or the user or delegated teammate changes.
        distinctUntilChanged(
          (prevParams, nextParams) =>
            prevParams.conversationId === nextParams.conversationId &&
            prevParams.cellsContext.teammateId === nextParams.cellsContext.teammateId,
        ),
        // Cancel any previous mark as read when we switch conversations or unmount.
        switchMap((params) =>
          timer(delayTime).pipe(
            concatMap(() =>
              dispatch(
                toggleMarkConversationAsSeenAction([params.conversationId], true, {
                  cellsContext: params.cellsContext,
                }),
              ),
            ),
          ),
        ),
      )
      .subscribe();

    return () => subscription.unsubscribe();
  }, [dispatch, isImpersonating, markAsReadDelay, params$]);

  useEffect(() => {
    params$.next({conversationId, cellsContext});
  });
}
